import { useCallback, useState } from 'react'
import { readKakaoRestKey } from '@/shared/lib/kakaoKey'
import {
  ENDPOINT,
  KEYWORD_ENDPOINT,
  PAGE_SIZE,
  REGION_ENDPOINT,
} from '@/public/features/problem/constants/kakaoLocal'

interface KakaoAddressDocument {
  address_name: string
  address: {
    address_name: string
    h_code: string
    b_code: string
    region_3depth_h_name: string
    region_3depth_name: string
  } | null
  road_address: { address_name: string; zone_no: string } | null
  x: string
  y: string
}

interface KakaoPlaceDocument {
  id: string
  place_name: string
  address_name: string
  road_address_name: string
  x: string
  y: string
}

export interface AddressCandidate {
  kind: 'address' | 'place'
  id?: string
  placeName?: string
  text: string
  jibun: string
  regionCode: string
  regionName: string
  legalCode: string
  legalName: string
  postalCode?: string
  lat: number
  lng: number
}

function fromAddress(doc: KakaoAddressDocument): AddressCandidate | null {
  const a = doc.address
  // 주소 객체 자체가 없으면 쓸 수 없다. `h_code` 가 비어 있는 것은 고를 때 좌표로 채운다.
  if (a === null) return null
  const zone = doc.road_address?.zone_no?.trim() ?? ''
  return {
    kind: 'address',
    text: doc.road_address?.address_name ?? doc.address_name,
    jibun: a.address_name,
    regionCode: a.h_code,
    regionName: a.region_3depth_h_name,
    legalCode: a.b_code,
    legalName: a.region_3depth_name,
    // 빈 문자열을 담지 않는다 — 없으면 키 자체가 없어야 본문에서도 빠진다.
    ...(zone.length > 0 ? { postalCode: zone } : {}),
    lat: Number(doc.y),
    lng: Number(doc.x),
  }
}

function fromPlace(doc: KakaoPlaceDocument): AddressCandidate {
  const road = doc.road_address_name.trim()
  return {
    kind: 'place',
    id: doc.id,
    placeName: doc.place_name,
    text: road.length > 0 ? road : doc.address_name,
    jibun: doc.address_name,
    regionCode: '',
    regionName: '',
    legalCode: '',
    legalName: '',
    lat: Number(doc.y),
    lng: Number(doc.x),
  }
}

export function addressMetaText(
  adminName: string,
  legalName: string,
  postalCode?: string,
): string {
  return [
    adminName.length > 0 ? `행정동 ${adminName}` : '',
    legalName.length > 0 ? `법정동 ${legalName}` : '',
    postalCode !== undefined && postalCode.length > 0 ? `우편번호 ${postalCode}` : '',
  ]
    .filter((part) => part.length > 0)
    .join(' · ')
}

export interface KakaoAddressSearch {
  results: AddressCandidate[]
  isLoading: boolean
  error: string | null
  hasSearched: boolean
  search: (query: string) => Promise<void>
  resolve: (c: AddressCandidate) => Promise<AddressCandidate>
  clear: () => void
}

export function useKakaoAddressSearch(): KakaoAddressSearch {
  const [results, setResults] = useState<AddressCandidate[]>([])
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  const clear = useCallback(() => {
    setResults([])
    setError(null)
    setHasSearched(false)
  }, [])

  const search = useCallback(async (query: string) => {
    const q = query.trim()
    if (q.length === 0) return

    const key = readKakaoRestKey()
    if (key === null) {
      setError('주소 검색을 쓸 수 없습니다. 관리자에게 알려주세요.')
      setResults([])
      setHasSearched(true)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const headers = { Authorization: `KakaoAK ${key}` }
      const qs = `query=${encodeURIComponent(q)}&size=${PAGE_SIZE}`
      const [addr, place] = await Promise.allSettled([
        fetch(`${ENDPOINT}?${qs}`, { headers }),
        fetch(`${KEYWORD_ENDPOINT}?${qs}`, { headers }),
      ])

      const rows: AddressCandidate[] = []

      if (addr.status === 'fulfilled' && addr.value.ok) {
        const body = (await addr.value.json()) as { documents?: KakaoAddressDocument[] }
        for (const doc of body.documents ?? []) {
          const row = fromAddress(doc)
          if (row !== null) rows.push(row)
        }
      }

      if (place.status === 'fulfilled' && place.value.ok) {
        const body = (await place.value.json()) as { documents?: KakaoPlaceDocument[] }
        const seen = new Set(rows.map((r) => r.jibun))
        for (const doc of body.documents ?? []) {
          const row = fromPlace(doc)
          if (seen.has(row.jibun)) continue
          rows.push(row)
        }
      }

      // 둘 다 실패했을 때만 오류다 — 한쪽이라도 답했으면 그 결과로 간다.
      if (
        rows.length === 0 &&
        !(addr.status === 'fulfilled' && addr.value.ok) &&
        !(place.status === 'fulfilled' && place.value.ok)
      ) {
        // 카카오 오류 본문은 사람에게 보여 줄 글이 아니다 — 우리 문장으로 바꾼다.
        setError('주소를 찾지 못했습니다. 잠시 후 다시 시도해주세요.')
        setResults([])
        return
      }

      setResults(rows)
    } catch {
      setError('주소 검색에 연결하지 못했습니다. 네트워크를 확인해주세요.')
      setResults([])
    } finally {
      setLoading(false)
      setHasSearched(true)
    }
  }, [])

  const resolve = useCallback(async (c: AddressCandidate): Promise<AddressCandidate> => {
    if (c.regionCode.length > 0) return c

    const key = readKakaoRestKey()
    if (key === null) return c
    try {
      const url = `${REGION_ENDPOINT}?x=${c.lng}&y=${c.lat}`
      const res = await fetch(url, { headers: { Authorization: `KakaoAK ${key}` } })
      if (!res.ok) return c
      const body = (await res.json()) as {
        documents?: Array<{ region_type: string; code: string; region_3depth_name: string }>
      }
      const docs = body.documents ?? []
      const h = docs.find((d) => d.region_type === 'H')
      if (h === undefined) return c
      // 법정동도 이 응답에 함께 온다(`B`) — 장소 결과에는 없던 값이라 여기서 채운다.
      const b = docs.find((d) => d.region_type === 'B')
      return {
        ...c,
        regionCode: h.code,
        regionName: h.region_3depth_name,
        ...(c.legalCode.length === 0 && b !== undefined
          ? { legalCode: b.code, legalName: b.region_3depth_name }
          : {}),
      }
    } catch {
      // 되묻지 못하면 코드 없는 채로 돌려준다 — 부르는 쪽이 「고를 수 없다」로 다룬다.
      return c
    }
  }, [])

  return { results, isLoading, error, hasSearched, search, resolve, clear }
}
